import React, { useEffect, useState, useMemo } from "react";
import ReactQuill, { Quill } from "react-quill";
import { useDispatch, useSelector } from "react-redux";
import { notification, Spin } from "antd";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import quillEmoji from "quill-emoji";

import { Button, Box } from "components";
import { SendCompleteIcon } from "icons";
import {
  GetConsentStatementActionCreators,
  PostConsentStatementActionCreators,
} from "redux/rootActions";
import {
  selectIsLoadingConsentStatement,
  selectConsentStatementErrorMessage,
  consentStatementDataResponse,
} from "redux/consentStatement/getConsentStatement/getConsentStatementStates";
import {
  selectIsPostingStatement,
  selectPostStatementErrorMessage,
  postStatementResponse,
} from "redux/consentStatement/postConsentStatement/postConsentStatementStates";

import { EditorWrapper } from "./ConsentStatement.style";

import "react-quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";
const { EmojiBlot, ShortNameEmoji, ToolbarEmoji, TextAreaEmoji } = quillEmoji;
const fontSizeArr = [
  "8px",
  "9px",
  "10px",
  "11px",
  "12px",
  "13px",
  "14px",
  "15px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "32px",
  "36px",
  "40px",
  false,
];

const Size = Quill.import("attributors/style/size");
const icons = Quill.import("ui/icons");
const BlockPrototype = Quill.import("blots/block");
const BlockEmbed = Quill.import("blots/block/embed");

class DivBlot extends BlockEmbed {}
DivBlot.blotName = "p";
DivBlot.tagName = "span";

class CustomBlock extends BlockPrototype {
  constructor(domNode: any, value: any) {
    super(domNode, value);
    this.format("size", "18px");
  }

  static tagName = "P";

  format(name: any, value: any) {
    if (name === "size") {
      this.domNode.style.fontSize = value;
    } else {
      super.format(name, value);
    }
  }
}

Size.whitelist = [
  "8px",
  "9px",
  "10px",
  "11px",
  "12px",
  "13px",
  "14px",
  "15px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "32px",
  "36px",
  "40px",
];
icons[
  "undo"
] = `<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<g>
<path d="M10.9 9.4C9.2 9.7 7.7 10.3 6.3 11.4L3.5 8.5V15.5H10.5L7.8 12.8C11.5 10.2 16.6 11 19.3 14.7C19.5 15 19.7 15.2 19.8 15.5L21.6 14.6C19.4 10.8 15.2 8.7 10.9 9.4Z" fill="black"/>
</g>
</svg>
`;
icons[
  "redo"
] = `<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<g>
<path d="M14.1 9.4C15.8 9.7 17.3 10.3 18.7 11.4L21.5 8.5V15.5H14.5L17.2 12.8C13.5 10.1 8.4 11 5.8 14.7C5.6 15 5.4 15.2 5.3 15.5L3.5 14.6C5.6 10.8 9.8 8.7 14.1 9.4Z" fill="black"/>
</g>
</svg>
`;
icons[
  "link"
] = `<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11.5 17H7.5C6.17392 17 4.90215 16.4732 3.96447 15.5355C3.02678 14.5979 2.5 13.3261 2.5 12C2.5 10.6739 3.02678 9.40215 3.96447 8.46447C4.90215 7.52678 6.17392 7 7.5 7H11.5V9H7.5C6.70435 9 5.94129 9.31607 5.37868 9.87868C4.81607 10.4413 4.5 11.2044 4.5 12C4.5 12.7956 4.81607 13.5587 5.37868 14.1213C5.94129 14.6839 6.70435 15 7.5 15H11.5V17ZM17.5 7H13.5V9H17.5C18.2956 9 19.0587 9.31607 19.6213 9.87868C20.1839 10.4413 20.5 11.2044 20.5 12C20.5 12.7956 20.1839 13.5587 19.6213 14.1213C19.0587 14.6839 18.2956 15 17.5 15H13.5V17H17.5C18.8261 17 20.0979 16.4732 21.0355 15.5355C21.9732 14.5979 22.5 13.3261 22.5 12C22.5 10.6739 21.9732 9.40215 21.0355 8.46447C20.0979 7.52678 18.8261 7 17.5 7ZM16.5 11H8.5V13H16.5V11Z" fill="black" />
</svg >
    `;

Quill.register(Size, true);
Quill.register(
  {
    "formats/emoji": EmojiBlot,
    "modules/emoji-shortname": ShortNameEmoji,
    "modules/emoji-toolbar": ToolbarEmoji,
    "modules/emoji-textarea": TextAreaEmoji,
  },
  true
);
// Quill.register(DivBlot);
Quill.register(CustomBlock, true);

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "indent",
  "link",
  "color",
  "image",
  "align",
  "undo",
  "redo",
  "emoji",
  "sub",
  "super",
  "size",
];

const CustomToolbar = () => (
  <div id="toolbar">
    <div className="ql-formats">
      <select
        className="ql-header"
        defaultValue={""}
        onChange={(e) => e.persist()}
      >
        <option value="1"></option>
        <option value="2"></option>
        <option value="3"></option>
        <option value="4"></option>
        <option value="5"></option>
        <option value="6"></option>
        <option selected></option>
      </select>
      <select className="ql-size">
        {Size.whitelist.map((size: any, index: number) => (
          <option key={index} value={size} selected={size.includes("18px")}>
            {size}
          </option>
        ))}
      </select>
      <button className="ql-align" value="justify"></button>
      <button className="ql-align" value="center"></button>
      <button className="ql-bold"></button>
      <button className="ql-italic"></button>
      <button className="ql-underline"></button>
      <button className="ql-strike"></button>
      <button className="ql-list" value="ordered"></button>
      <button className="ql-list" value="bullet"></button>
      <button className="ql-link"></button>
      <button className="ql-image"></button>
      <button className="ql-emoji"></button>
    </div>
    <div className="ql-formats">
      <button className="ql-undo"></button>
      <button className="ql-redo"></button>
    </div>
  </div>
);

const CreateConsentStatement: React.FC = () => {
  let quillEditor: any;
  const dispatch = useDispatch();

  const isLoadingStatement = useSelector(selectIsLoadingConsentStatement);
  const postingStatement = useSelector(selectIsPostingStatement);

  const getStatementError = useSelector(selectConsentStatementErrorMessage);
  const postStatementError = useSelector(selectPostStatementErrorMessage);

  const getStatementData = useSelector(consentStatementDataResponse);
  const postStatementData = useSelector(postStatementResponse);

  const [code, setCode] = useState(
    // '<p><strong style="font-size: 24px;">オンラインかかりつけ医システムの利用に関する同意書 </strong></p><p>オンラインかかりつけ医システム（以下、「本機能」といいます。）は、内閣府が実施する「先端的サービスの開発・構築や先端的サービス実装のためのデータ連携等に関する調査事業」の一環として、つくば市および茅野市在住の住民の方のみが利用できる機能です。本機能の利用は、以下の内容に同意した上で行ってください。</p><p><br></p><p><strong>＜事業の名称＞</strong></p><p>遠隔医療アプリを活用した持続可能性の高い小児オンラインかかりつけ医体制の構築（以下、「本事業」といいます。）</p><p><br></p><p><strong>&nbsp;＜本事業の概要＞</strong></p><p>本事業では、かかりつけ診療所の時間外の健康医療相談対応に遠隔医療アプリに登録する医師がオンライン上（テキストチャット）で対応し、遠隔健康医療相談の結果、診療を要する場合はかかりつけ診療所の勤務医が対応し、救急外来の受診が適当と認められる場合は救急医療施設に行くよう勧めます。かかりつけ診療所の医師としてオンライン診療を行う医師は、つくば市と茅野市の特性を踏まえて確保（雇用契約を締結）し、地域の事情に即した小児オンラインかかりつけ医体制の構築を目指します。また、遠隔医療アプリ（サービス）を活用した小児オンラインかかりつけ医体制の導入によって、保険者が負担する療養給付費、地方自治体が負担する福祉助成や夜間救急外来に係る運営費用等の医療費削減効果があるかを測定・評価します。評価結果に基づいたサービス提供者への成果報酬、患者への市販薬購入用時限ポイント付与、かかりつけ診療所へのセルフケア・セルフメディケーション説明料付与についてその効果と是非を検証します。</p><p><br></p><p><strong> ＜本事業の内容＞</strong></p><p><br></p><p>・本事業にかかわる遠隔医療アプリの遠隔健康医療相談機能、かかりつけ医登録機能を無償で提供します。</p><p>※オンライン診療にかかる保険診療費用およびオンライン診療機能の利用料はユーザー負担となります。</p><p>・本機能の利用者（以下、「ユーザー」といいます。）が遠隔医療アプリに登録した基本情報（既往歴、服薬状況、アレルギー歴等）および、健康医療相談内容は、本事業に参加する医療機関に提供します。また、当該情報は、本事業実施の構成員である株式会社リーバー、筑波大学、東京大学、茅野市、諏訪中央病院、つくば市（以下、構成員）が事業評価および研究開発のために使用します。※取得した情報を公表する際には、個人が特定できない形式で行います。</p><p>・オンライン診療を受診する際には、ユーザーが遠隔医療アプリに登録した被保険者証情報、福祉医療費受給者証情報を本事業に参加する医療機関に提供します。なお、ユーザーが登録した情報に誤りがあった場合、構成員が茅野市に当該情報を照会します。</p><p>・オンライン診療の際のカルテ情報は、ユーザーのオンライン診療を担当する医師が所属する医療機関に提供します。</p><p>・本機能を利用する前中後でアンケートを実施し、本事業の実施に関わる関係者が事業評価および研究開発のために使用します。※アンケートで取得した情報を公表する際には、個人が特定できない形式で行います。</p><p>・【茅野市在住の方を対象として実施】遠隔健康医療相談機能にて、市販薬購入のための時限ポイントが付与された場合、当該ポイントの利用状況を構成員が事業評価および研究開発のために使用します。※取得した情報を公表する際には、個人が特定できない形式で行います。</p><p>・【茅野市在住の方を対象として実施】遠隔医療アプリユーザーの医療費や福祉助成に関する情報を茅野市より取得し、構成員が、遠隔医療アプリを住民が利用したことによる医療費削減および福祉助成削減効果の検証および研究開発に使用します。※取得した情報を公表する際には、個人が特定できない形式で行います。</p><p><br></p><p><strong>期間</strong></p><p><br></p><p>2023 年 11 月 20 日〜2024 年 3 月 15 日</p><p>※本機能の無償提供は 2024 年 3 月 16 日に自動で解除されます。2024 年 3 月 16 日以降、自動的に課金されることはありません。</p><p><br></p><p><strong>＜本機能に関する同意事項＞</strong></p><p><br></p><ol><li>本事業実施期間中、本機能を無償で利用すること。</li><li>ユーザーが遠隔医療アプリに登録した基本情報（既往歴、服薬状況、アレルギー歴等）および健康医療相談内容を、本事業に参加する医療機関に提供すること。当該情報を、構成員が事業評価および研究開発のために使用すること。</li><li>オンライン診療を受診する際に、ユーザーが遠隔医療アプリに登録した被保険者証情報、福祉医療費受給者証情報を本事業に参加する医療機関に提供すること。茅野市住民においては、ユーザーが登録した情報に誤りがあった場合、構成員が茅野市に当該情報を照会すること。</li><li>オンライン診療の際のカルテ情報を、ユーザーのオンライン診療を担当する医師が所属する医療機関に提供すること。</li><li>本機能を利用する前中後で実施するアンケートに回答すること。</li><li>【茅野市在住の方のみ】遠隔健康医療相談機能にて、市販薬購入のための時限ポイントが付与された場合、当該ポイントの利用状況を構成員が事業評価および研究開発のために使用すること。</li><li>【茅野市在住の方のみ】遠隔医療アプリユーザーの医療費や福祉助成に関する情報を構成員が茅野市より取得し、構成員が事業評価および研究開発に使用すること。</li></ol><p><br></p><p>お問い合わせ先：株式会社リーバー</p><p>TEL:029-896-6263 （平日 10:00-12:00,14:00-17:00）</p><p>E-mail:info@leber.jp</p>'
    ""
  );

  const modules = useMemo(
    () => ({
      history: { delay: 200, maxStack: 500, userOnly: true },
      toolbar: {
        container: "#toolbar",
        // [
        //   [
        //     { header: [1, 2, 3, 4, 5, 6, false] },
        //     { size: fontSizeArr },
        //     // { align: "right" },
        //     { align: "justify" },
        //     { align: "center" },
        //     "bold",
        //     "italic",
        //     "underline",
        //     "strike",
        //     { list: "ordered" },
        //     { list: "bullet" },
        //     "link",
        //     "image",
        //     "emoji",
        //     // { list: "bullet" }
        //     // { size: ["14px", "16px", "18px"] },
        //   ],
        //   // [{ font: [] }],
        //   // [{ align: ["right"] }, { align: ["center"] }, { align: ["justify"] }],
        //   // [{ script: "sub" }, { script: "super" }],
        //   // , { header: "2" }, { font: [] }],
        //   // [{ size: [] }],
        //   ["undo", "redo"],
        //   // ["bold", "italic", "underline", "strike", "blockquote"],
        //   // [
        //   //     { indent: "-1" },
        //   //     { indent: "+1" },
        //   // ],
        //   // ["emoji"],
        //   // ["clean"],
        // ],
        handlers: {
          undo: () => {
            return quillEditor?.history.undo();
          },
          redo: () => {
            return quillEditor?.history.redo();
          },
        },
      },
      clipboard: {
        // toggle to add extra line breaks when pasting HTML:
        matchVisual: false,
      },
      "emoji-toolbar": true,
      "emoji-textarea": false,
      "emoji-shortname": true,
    }),
    []
  );

  const handleProcedureContentChange = (content: any) => {
    setCode(content);
  };

  useEffect(() => {
    setCode("");
    dispatch(PostConsentStatementActionCreators.handleResetAction());
    dispatch(GetConsentStatementActionCreators.getConsentStatementAction());
  }, []);

  useEffect(() => {
    if (getStatementData && !isLoadingStatement) {
      setCode(getStatementData.result?.term_content || "");
      dispatch(GetConsentStatementActionCreators.handleResetDataAction());
    }
  }, [getStatementData, isLoadingStatement]);

  useEffect(() => {
    if (getStatementError) {
      setCode("");
      dispatch(GetConsentStatementActionCreators.handleResetDataAction());
    }
    if (postStatementError) {
      dispatch(PostConsentStatementActionCreators.handleResetAction());
      notification.success({
        placement: "bottomRight",
        message: "保存が失敗しました。",
        icon: <SendCompleteIcon />,
      });
    }
  }, [getStatementError, postStatementError]);

  useEffect(() => {
    if (postStatementData && !postingStatement) {
      notification.success({
        placement: "bottomRight",
        message: "保存しました。",
        icon: <SendCompleteIcon />,
      });
      dispatch(PostConsentStatementActionCreators.handleResetAction());
    }
  }, [postStatementData, postingStatement]);

  return (
    <Box title="同意文作成" subTitle="" padding="40px">
      <EditorWrapper>
        <Spin spinning={isLoadingStatement}>
          <div className="text-editor">
            <CustomToolbar />
            <ReactQuill
              ref={(el) => {
                if (el) quillEditor = el.getEditor();
              }}
              theme="snow"
              placeholder=""
              modules={modules}
              // formats={formats}
              value={code}
              onChange={handleProcedureContentChange}
            />
          </div>
        </Spin>
      </EditorWrapper>
      <div style={{ width: "100%", display: "flex" }}>
        <Button
          onClick={() => {
            dispatch(
              PostConsentStatementActionCreators.postConsentStatementAction({
                term_content: code,
              })
            );
          }}
          background="#2AC769"
          color="#ffffff"
          name="保存"
          border="none"
          bdr="6px"
          fontSize={16}
          fontWeight={700}
          style={{
            margin: "36px 0px 0px auto",
            padding: "5px 74px 4px",
          }}
          disabled={postingStatement || isLoadingStatement || !code}
        />
      </div>
    </Box>
  );
};

export default CreateConsentStatement;
